import { gql, useLazyQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import * as React from 'react';

import { logWarning } from '@/imports/logging/ClientLogger';
import { useDownloadErrorFile } from '@/imports/ui/hooks/useDownloadErrorFile/useDownloadErrorFile';

import {
  useDownloadUserGroupLinkCsvErrors_getErrorsForUserGroupLinkCsvUpload,
  useDownloadUserGroupLinkCsvErrors_getErrorsForUserGroupLinkCsvUploadVariables,
} from './graphql-types/useDownloadUserGroupLinkCsvErrors_getErrorsForUserGroupLinkCsvUpload';

const USER_GROUP_LINK_CSV_UPLOAD_ERROR = gql`
  query useDownloadUserGroupLinkCsvErrors_getErrorsForUserGroupLinkCsvUpload(
    $input: GetErrorsForUserGroupLinkCsvUploadInput!
  ) {
    getErrorsForUserGroupLinkCsvUpload(input: $input) {
      errorFileUrl
    }
  }
`;

export const useDownloadUserGroupLinkCsvErrors = (options: {
  validRoutes: string[];
}): void => {
  const router = useRouter();
  const userGroupLinkCsvUploadId = router.query.userGroupLinkCsvUploadId;

  const isValidRoute = options.validRoutes.reduce(
    (prev, route) => prev || router.pathname.includes(route),
    false
  );

  const [getErrorsForUserGroupLinkCsvUpload, { data, called }] = useLazyQuery<
    useDownloadUserGroupLinkCsvErrors_getErrorsForUserGroupLinkCsvUpload,
    useDownloadUserGroupLinkCsvErrors_getErrorsForUserGroupLinkCsvUploadVariables
  >(USER_GROUP_LINK_CSV_UPLOAD_ERROR);

  React.useEffect(() => {
    if (
      !router.isReady ||
      !userGroupLinkCsvUploadId ||
      called ||
      !isValidRoute
    ) {
      return;
    }

    getErrorsForUserGroupLinkCsvUpload({
      variables: {
        input: {
          data: {
            userGroupLinkCsvUploadId: userGroupLinkCsvUploadId as string,
          },
        },
      },
    });
  }, [
    router,
    called,
    userGroupLinkCsvUploadId,
    getErrorsForUserGroupLinkCsvUpload,
    isValidRoute,
  ]);

  React.useEffect(() => {
    if (data && !data.getErrorsForUserGroupLinkCsvUpload.errorFileUrl) {
      logWarning(
        `Could not find error file for userGroupLinkCsvUpload: ${userGroupLinkCsvUploadId}`
      );
    }
  }, [data, userGroupLinkCsvUploadId]);

  useDownloadErrorFile({
    fileUrl: data?.getErrorsForUserGroupLinkCsvUpload.errorFileUrl ?? undefined,
    shouldDownload: !!userGroupLinkCsvUploadId,
  });
};
